package br.com.heyagenda.data.remote

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

@Serializable
data class ExpenseDto(
    val id: String = "",
    val description: String = "",
    val amount: Double = 0.0,
    val priceFormatted: String = "",
    val date: String = "",
    val category: String = "OUTROS",
    val createdAtIso: String = ""
)

interface FirestoreExpenseDataSource {
    fun observeExpenses(uid: String): Flow<List<ExpenseDto>>
    suspend fun getExpenses(uid: String): List<ExpenseDto>
    fun observeExpensesInRange(uid: String, startDateIso: String, endDateIso: String): Flow<List<ExpenseDto>>
    suspend fun getExpensesInRange(uid: String, startDateIso: String, endDateIso: String): List<ExpenseDto>
    suspend fun saveExpense(uid: String, expense: ExpenseDto): Result<Unit>
    suspend fun deleteExpense(uid: String, expenseId: String): Result<Unit>
}

class FirestoreExpenseDataSourceImpl : FirestoreExpenseDataSource {

    private val firestore: FirebaseFirestore? by lazy {
        try {
            Firebase.firestore
        } catch (e: Throwable) {
            println(">>> [FirestoreExpense ERROR] Failed to get Firebase.firestore: ${e.message}")
            null
        }
    }

    override suspend fun getExpenses(uid: String): List<ExpenseDto> {
        val fs = firestore ?: return emptyList()
        return try {
            val snapshot = fs.collection("professionals")
                .document(uid)
                .collection("expenses")
                .get()
            val list = snapshot.documents.mapNotNull { parseExpenseDto(it) }
            println(">>> [FirestoreExpense GET] Fetched ${list.size} expenses for $uid")
            list
        } catch (e: Throwable) {
            println(">>> [FirestoreExpense GET ERROR] $uid: ${e.message}")
            emptyList()
        }
    }

    override fun observeExpenses(uid: String): Flow<List<ExpenseDto>> = flow {
        val fs = firestore
        if (fs == null) {
            emit(emptyList())
            return@flow
        }

        val direct = getExpenses(uid)
        emit(direct)

        try {
            fs.collection("professionals")
                .document(uid)
                .collection("expenses")
                .snapshots
                .map { snapshot ->
                    snapshot.documents.mapNotNull { parseExpenseDto(it) }
                }
                .catch { e ->
                    println(">>> [FirestoreExpense SNAPSHOT ERROR] $uid: ${e.message}")
                }
                .collect { list ->
                    emit(list)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreExpense SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    override suspend fun getExpensesInRange(uid: String, startDateIso: String, endDateIso: String): List<ExpenseDto> {
        val fs = firestore ?: return emptyList()
        return try {
            val snapshot = fs.collection("professionals")
                .document(uid)
                .collection("expenses")
                .where {
                    "date" greaterThanOrEqualTo startDateIso
                    "date" lessThanOrEqualTo endDateIso
                }
                .get()
            val list = snapshot.documents.mapNotNull { parseExpenseDto(it) }
            list
        } catch (e: Throwable) {
            println(">>> [FirestoreExpense GET RANGE ERROR] $uid / $startDateIso..$endDateIso: ${e.message}")
            emptyList()
        }
    }

    override fun observeExpensesInRange(uid: String, startDateIso: String, endDateIso: String): Flow<List<ExpenseDto>> = flow {
        val fs = firestore
        if (fs == null) {
            emit(emptyList())
            return@flow
        }
        val direct = getExpensesInRange(uid, startDateIso, endDateIso)
        emit(direct)

        try {
            fs.collection("professionals")
                .document(uid)
                .collection("expenses")
                .where {
                    "date" greaterThanOrEqualTo startDateIso
                    "date" lessThanOrEqualTo endDateIso
                }
                .snapshots
                .map { snapshot ->
                    snapshot.documents.mapNotNull { parseExpenseDto(it) }
                }
                .catch { e ->
                    println(">>> [FirestoreExpense RANGE SNAPSHOT ERROR] $uid / $startDateIso..$endDateIso: ${e.message}")
                }
                .collect { list ->
                    emit(list)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreExpense RANGE SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    override suspend fun saveExpense(uid: String, expense: ExpenseDto): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            val docRef = if (expense.id.isBlank()) {
                fs.collection("professionals").document(uid).collection("expenses").document
            } else {
                fs.collection("professionals").document(uid).collection("expenses").document(expense.id)
            }
            val finalDto = expense.copy(id = docRef.id)

            docRef.set(
                mapOf(
                    "id" to finalDto.id,
                    "description" to finalDto.description,
                    "amount" to finalDto.amount,
                    "priceFormatted" to finalDto.priceFormatted,
                    "date" to finalDto.date,
                    "category" to finalDto.category,
                    "createdAtIso" to finalDto.createdAtIso
                )
            )
            println(">>> [FirestoreExpense SUCCESS] Saved expense ${finalDto.id}")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreExpense SAVE ERROR] ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun deleteExpense(uid: String, expenseId: String): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            fs.collection("professionals")
                .document(uid)
                .collection("expenses")
                .document(expenseId)
                .delete()
            println(">>> [FirestoreExpense DELETE SUCCESS] Deleted expense $expenseId")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreExpense DELETE ERROR] ${e.message}")
            Result.failure(e)
        }
    }

    private fun parseExpenseDto(doc: DocumentSnapshot): ExpenseDto? {
        return try {
            val id = doc.id
            val description = doc.get<String?>("description") ?: ""
            val amount = try { doc.get<Double?>("amount") ?: 0.0 } catch (_: Throwable) { 0.0 }
            val priceFormatted = doc.get<String?>("priceFormatted") ?: ""
            val date = doc.get<String?>("date") ?: ""
            val category = doc.get<String?>("category") ?: "OUTROS"
            val createdAtIso = doc.get<String?>("createdAtIso") ?: ""

            ExpenseDto(
                id = id,
                description = description,
                amount = amount,
                priceFormatted = priceFormatted,
                date = date,
                category = category,
                createdAtIso = createdAtIso
            )
        } catch (e: Throwable) {
            try {
                doc.data<ExpenseDto>().copy(id = doc.id)
            } catch (_: Throwable) {
                null
            }
        }
    }
}
